import { FREE_PLAN_PRICE, PRO_PLAN_MONTHLY_PRICE, ENTERPRISE_PLAN_MONTHLY_PRICE, PRICING_CONFIG } from '../config/pricing.config';

export interface RouteSeoMeta {
  title: string;
  description: string;
  keywords?: string;
  canonical: string;
  ogType?: string;
  ogImage?: string;
  jsonLd?: Record<string, any> | Array<Record<string, any>>;
}

const DOMAIN = 'https://zipytiny.app';
const DEFAULT_OG_IMAGE = `${DOMAIN}/og-image.png`;

export const ALL_ROUTES = [
  '/',
  '/study',
  '/blog',
  '/blog/best-ai-tools-video-lecture-meeting-notes',
  '/blog/why-i-built-zipytiny',
  '/blog/turn-video-lecture-to-study-notes',
  '/blog/ai-tool-make-flashcards-slides',
  '/blog/convert-zoom-recording-quiz',
  '/blog/generate-study-guide-syllabus-pdf',
  '/blog/visual-learners-video-mind-map-generator',
  '/features',
  '/features/mind-maps',
  '/features/flashcards',
  '/features/study-notes',
  '/features/quiz-gen',
  '/features/podcast-gen',
  '/features/meeting-notes',
  '/features/action-items',
  '/features/presentation-export',
  '/features/executive-summaries',
  '/use-cases/sales-calls',
  '/use-cases/client-meeting-recap',
  '/use-cases/recording-to-powerpoint',
  '/vs/otter-ai',
  '/youtube-quiz',
  '/chrome-extension',
  '/tools',
  '/tools/youtube-lecture-summarizer',
  '/tools/pdf-study-guide-generator',
  '/tools/interactive-ai-tutor',
  '/pricing',
  '/faq',
  '/admin'
];

export function normalizeApexCanonical(url?: string): string {
  if (!url) return `${DOMAIN}/`;

  let normalized = url.trim();
  if (normalized.startsWith('/')) {
    normalized = `${DOMAIN}${normalized}`;
  } else {
    normalized = normalized.replace(/^https?:\/\/(?:www\.)?zipytiny\.app/i, DOMAIN);
  }

  // Forcefully strip any remaining www.zipytiny.app
  normalized = normalized.replace(/^(https?:\/\/)?www\.zipytiny\.app/i, DOMAIN);

  // Preserve query/hash if any, ensure trailing slash on path
  const [basePath, ...rest] = normalized.split(/[?#]/);
  const suffix = normalized.slice(basePath.length);
  const segments = basePath.split('/');
  const lastSeg = segments[segments.length - 1] || '';

  if (!lastSeg.includes('.') && !basePath.endsWith('/')) {
    normalized = `${basePath}/${suffix}`;
  }

  return normalized;
}

function resolveRouteSeoMeta(urlPath: string): RouteSeoMeta {
  const cleanPath = urlPath.split('?')[0].replace(/\/$/, '') || '/';

  if (cleanPath === '/admin') {
    return {
      title: 'Admin Console | Zipytiny',
      description: 'Zipytiny administrative operations, database diagnostics, user telemetry, and support console.',
      canonical: `${DOMAIN}/admin/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE
    };
  }

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'Zipytiny',
    'url': DOMAIN,
    'logo': `${DOMAIN}/icon.png`,
    'sameAs': [
      'https://www.linkedin.com/company/zipytiny/',
      'https://www.facebook.com/zipytiny',
      'https://www.instagram.com/zipytiny.app/',
      'https://www.youtube.com/@zipytiny',
      'https://www.tiktok.com/@zipytiny.app',
      'https://www.producthunt.com/products/zipytiny'
    ]
  };

  // Public Study Library: /study
  if (cleanPath === '/study') {
    return {
      title: 'Open AI Study Library — Free Video Notes, Quizzes & Anki Decks | Zipytiny',
      description: 'Explore verified study guides, active recall quizzes, and downloadable Anki decks created from top university lectures, podcasts, and technical seminars.',
      keywords: 'study library, lecture summaries, free anki decks, video study guides, cs50 notes, huberman lab summary',
      canonical: `${DOMAIN}/study/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          'name': 'Open AI Study Library | Zipytiny',
          'description': 'Searchable library of video summaries, practice quizzes, and Anki decks generated by Zipytiny.',
          'url': `${DOMAIN}/study/`,
          'publisher': orgSchema
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Study Library', 'item': `${DOMAIN}/study/` }
          ]
        }
      ]
    };
  }

  // Programmatic Study Guide: /study/:slug
  if (cleanPath.startsWith('/study/')) {
    const rawSlug = cleanPath.replace('/study/', '');
    const cleanTitle = rawSlug
      .replace(/-[a-zA-Z0-9_-]{8,}$/, '')
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ') || 'Lecture Study Guide';

    const formattedTitle = `${cleanTitle} — Summary, Key Takeaways, Quiz & Anki Deck | Zipytiny`;

    return {
      title: formattedTitle,
      description: `Complete study guide for ${cleanTitle}. Includes Cornell lecture summary, high-yield key takeaways, active recall quiz questions, and 1-click Anki deck export.`,
      keywords: `${cleanTitle.toLowerCase()}, study guide, key takeaways, anki deck, video summary, interactive quiz`,
      canonical: `${DOMAIN}${cleanPath}`,
      ogType: 'article',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'LearningResource',
          'name': formattedTitle,
          'description': `Study guide and quiz for ${cleanTitle}`,
          'learningResourceType': 'Study Guide',
          'educationalLevel': 'Higher Education',
          'url': `${DOMAIN}${cleanPath}`,
          'publisher': orgSchema
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Study Library', 'item': `${DOMAIN}/study/` },
            { '@type': 'ListItem', 'position': 3, 'name': cleanTitle, 'item': `${DOMAIN}${cleanPath}` }
          ]
        },
        {
          '@context': 'https://schema.org',
          '@type': 'Quiz',
          'name': `${cleanTitle} Diagnostic Quiz`,
          'description': `Practice test checking comprehension of ${cleanTitle}`,
          'publisher': orgSchema
        }
      ]
    };
  }

  // Shared Quiz /quiz/:id
  if (cleanPath.startsWith('/quiz/')) {
    const quizId = cleanPath.split('/')[2] || 'public-quiz';
    const formattedTitle = quizId !== 'public-quiz'
      ? `Interactive 10-Question Video Quiz | Zipytiny`
      : 'Interactive YouTube Video Quiz | Zipytiny';

    return {
      title: formattedTitle,
      description: 'Take this interactive 10-question quiz generated in 60 seconds from YouTube. Test your recall, get instant answer explanations, and share your score.',
      keywords: 'youtube quiz generator, interactive video quiz, ai multiple choice quiz, active recall test',
      canonical: `${DOMAIN}${cleanPath}`,
      ogType: 'article',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'Quiz',
          'name': formattedTitle,
          'description': 'Interactive 10-question quiz created from YouTube video with instant AI grading and explanations.',
          'publisher': orgSchema
        }
      ]
    };
  }

  // YouTube Quiz Generator landing page
  if (cleanPath === '/youtube-quiz') {
    return {
      title: 'Free YouTube Video to Interactive Quiz Generator | Zipytiny',
      description: 'Turn any YouTube video into an interactive 10-question quiz in 60 seconds. Free, instant, shareable, with detailed answer explanations.',
      keywords: 'youtube quiz generator, turn youtube into quiz, video to quiz ai, interactive multiple choice quiz generator, lecture quiz maker',
      canonical: `${DOMAIN}/youtube-quiz/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          'name': 'Zipytiny YouTube Video Quiz Generator',
          'url': `${DOMAIN}/youtube-quiz/`,
          'description': 'Turn any YouTube video into an interactive 10-question quiz in 60 seconds.',
          'applicationCategory': 'EducationalApplication',
          'operatingSystem': 'All',
          'offers': {
            '@type': 'Offer',
            'price': '0.00',
            'priceCurrency': 'USD'
          },
          'publisher': orgSchema
        }
      ]
    };
  }

  if (cleanPath.startsWith('/s/') || cleanPath.startsWith('/share/')) {
    const shareId = cleanPath.split('/')[2] || 'public-guide';
    const formattedTitle = shareId !== 'public-guide' 
      ? `Shared Study Workspace: ${shareId.replace(/[-_]/g, ' ')} | Zipytiny`
      : 'Shared AI Study Guide & Active Recall Deck | Zipytiny';

    return {
      title: formattedTitle,
      description: 'View this interactive study guide powered by Zipytiny. Features timestamped video summaries, key takeaways, flashcards, and concept mind maps.',
      keywords: 'shared study guide, public lecture notes, ai flashcards, active recall deck, zipytiny share',
      canonical: `${DOMAIN}${cleanPath}`,
      ogType: 'article',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'CreativeWork',
          'name': formattedTitle,
          'description': 'Interactive study guide with video summaries, active recall flashcards, and concept mind maps.',
          'publisher': orgSchema
        }
      ]
    };
  }

  if (cleanPath === '/blog/why-i-built-zipytiny') {
    return {
      title: 'Turn Any Video, PDF, or Lecture Into Study Notes in Seconds — Why I Built Zipytiny',
      description: 'The story behind Zipytiny: why manual study note creation wastes millions of hours, how active recall removes friction, and why I built an AI study workspace as a solo founder.',
      keywords: 'why i built zipytiny, zipytiny story, ai study workspace, active recall tool, lecture to study notes, solo founder',
      canonical: `${DOMAIN}/blog/why-i-built-zipytiny/`,
      ogType: 'article',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          'headline': 'Turn Any Video, PDF, or Lecture Into Study Notes in Seconds — Why I Built Zipytiny',
          'description': 'The story behind Zipytiny: why manual study note creation wastes millions of hours, how active recall removes friction, and why I built an AI study workspace as a solo founder.',
          'url': `${DOMAIN}/blog/why-i-built-zipytiny/`,
          'author': {
            '@type': 'Person',
            'name': 'Zipytiny Founder'
          },
          'publisher': orgSchema
        }
      ]
    };
  }

  if (cleanPath === '/blog/best-ai-tools-video-lecture-meeting-notes') {
    return {
      title: '15 Best AI Tools to Summarize Video Lectures & Meetings (2026 Guide) | Zipytiny',
      description: 'Compare the 15 best AI tools for summarizing YouTube lectures, Zoom meetings, and research seminars in 2026. From automated flashcard generation to interactive mind maps.',
      keywords: 'best ai summarizer for video, ai lecture notes generator, summarize zoom meetings ai, ai flashcards from video, video to mind map, zipytiny',
      canonical: `${DOMAIN}/blog/best-ai-tools-video-lecture-meeting-notes/`,
      ogType: 'article',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          'headline': '15 Best AI Tools to Summarize Video Lectures & Meetings (2026 Guide)',
          'description': 'Compare the 15 best AI tools for summarizing YouTube lectures, Zoom meetings, and research seminars in 2026.',
          'url': `${DOMAIN}/blog/best-ai-tools-video-lecture-meeting-notes/`,
          'author': { '@type': 'Person', 'name': 'Zipytiny Editorial Team' },
          'publisher': orgSchema
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Blog', 'item': `${DOMAIN}/blog/` },
            { '@type': 'ListItem', 'position': 3, 'name': '15 Best AI Summarizers', 'item': `${DOMAIN}/blog/best-ai-tools-video-lecture-meeting-notes/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/blog/turn-video-lecture-to-study-notes') {
    return {
      title: 'Video Lecture to Notes Converter: Turn Any Video into Study Notes | Zipytiny Blog',
      description: 'Master the 3-step workflow for transforming recorded webinars, university lectures, and YouTube tutorials into structured outlines, active recall flashcards, and concept maps.',
      keywords: 'video lecture to notes converter, convert video to notes, ai note taker for youtube, timestamped lecture summary, active recall study notes, university exam prep ai',
      canonical: `${DOMAIN}/blog/turn-video-lecture-to-study-notes/`,
      ogType: 'article',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          'headline': 'How to Convert Video Lectures into Notes (Free AI Method)',
          'name': 'How to Convert Video Lectures into Notes (Free AI Method)',
          'description': 'Master the 3-step workflow for transforming recorded webinars, university lectures, and YouTube tutorials into structured outlines and active recall flashcards.',
          'url': `${DOMAIN}/blog/turn-video-lecture-to-study-notes/`,
          'author': { '@type': 'Person', 'name': 'Zipytiny Editorial Team' },
          'publisher': orgSchema
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Blog', 'item': `${DOMAIN}/blog/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'How to Convert Video Lectures into Notes (Free AI Method)', 'item': `${DOMAIN}/blog/turn-video-lecture-to-study-notes/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/blog/ai-tool-make-flashcards-slides') {
    return {
      title: 'AI Flashcard Generators: How to Automatically Turn Slides & Videos into Study Decks | Zipytiny',
      description: 'Discover how automated AI flashcard generation accelerates revision prep. Learn the science of spaced repetition, automated Q&A card creation, and export workflows.',
      keywords: 'ai flashcard generator, make flashcards from video, auto flashcard maker, spaced repetition ai, slides to flashcards anki',
      canonical: `${DOMAIN}/blog/ai-tool-make-flashcards-slides/`,
      ogType: 'article',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          'headline': 'AI Flashcard Generators: How to Automatically Turn Slides & Videos into Study Decks',
          'description': 'Discover how automated AI flashcard generation accelerates revision prep with spaced repetition.',
          'url': `${DOMAIN}/blog/ai-tool-make-flashcards-slides/`,
          'author': { '@type': 'Person', 'name': 'Zipytiny Editorial Team' },
          'publisher': orgSchema
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Blog', 'item': `${DOMAIN}/blog/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'AI Flashcard Generators', 'item': `${DOMAIN}/blog/ai-tool-make-flashcards-slides/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/blog/convert-zoom-recording-quiz') {
    return {
      title: 'How to Convert Any Zoom Recording or Lecture into an Interactive Quiz Deck | Zipytiny',
      description: 'Step-by-step guide to generating multi-format quizzes (multiple choice, conceptual check, true/false) directly from meeting and lecture recordings.',
      keywords: 'turn video into quiz, ai quiz generator from lecture, zoom recording test generator, active recall quiz maker',
      canonical: `${DOMAIN}/blog/convert-zoom-recording-quiz/`,
      ogType: 'article',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          'headline': 'How to Convert Any Zoom Recording or Lecture into an Interactive Quiz Deck',
          'description': 'Step-by-step guide to generating multi-format quizzes directly from meeting and lecture recordings.',
          'url': `${DOMAIN}/blog/convert-zoom-recording-quiz/`,
          'author': { '@type': 'Person', 'name': 'Zipytiny Editorial Team' },
          'publisher': orgSchema
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Blog', 'item': `${DOMAIN}/blog/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'Convert Zoom to Quiz', 'item': `${DOMAIN}/blog/convert-zoom-recording-quiz/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/blog/generate-study-guide-syllabus-pdf') {
    return {
      title: 'How to Generate a Comprehensive Study Guide & Syllabus from Video Playlists | Zipytiny',
      description: 'Turn entire YouTube playlists or lecture series into cohesive, unified study guides with chapter breakdowns, executive summaries, and downloadable PDF decks.',
      keywords: 'generate study guide from playlist, youtube playlist to syllabus, ai lecture series summarizer, downloadable study pdf',
      canonical: `${DOMAIN}/blog/generate-study-guide-syllabus-pdf/`,
      ogType: 'article',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          'headline': 'How to Generate a Comprehensive Study Guide & Syllabus from Video Playlists',
          'description': 'Turn entire YouTube playlists or lecture series into cohesive, unified study guides with chapter breakdowns.',
          'url': `${DOMAIN}/blog/generate-study-guide-syllabus-pdf/`,
          'author': { '@type': 'Person', 'name': 'Zipytiny Editorial Team' },
          'publisher': orgSchema
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Blog', 'item': `${DOMAIN}/blog/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'Playlists to Study Guide', 'item': `${DOMAIN}/blog/generate-study-guide-syllabus-pdf/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/blog/visual-learners-video-mind-map-generator') {
    return {
      title: 'For Visual Learners: Generating Interactive Concept Mind Maps from Video Content | Zipytiny',
      description: 'Visual learners thrive with spatial hierarchy and concept mapping. Learn how Zipytiny transforms linear lectures into interactive semantic node graphs.',
      keywords: 'video to mind map, ai concept graph generator, visual learning tools, lecture concept visualizer, interactive mindmap ai',
      canonical: `${DOMAIN}/blog/visual-learners-video-mind-map-generator/`,
      ogType: 'article',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          'headline': 'For Visual Learners: Generating Interactive Concept Mind Maps from Video Content',
          'description': 'Learn how Zipytiny transforms linear lectures into interactive semantic node graphs for visual learners.',
          'url': `${DOMAIN}/blog/visual-learners-video-mind-map-generator/`,
          'author': { '@type': 'Person', 'name': 'Zipytiny Editorial Team' },
          'publisher': orgSchema
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Blog', 'item': `${DOMAIN}/blog/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'Visual Mind Maps', 'item': `${DOMAIN}/blog/visual-learners-video-mind-map-generator/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/blog') {
    return {
      title: 'Zipytiny Learning Hub & Study Engineering Blog | Zipytiny',
      description: 'Deep dives, evidence-based study workflows, and technical breakthroughs in AI-accelerated learning, active recall, and knowledge synthesis.',
      keywords: 'ai study blog, learning workflows, active recall techniques, video summarization guides, zipytiny updates',
      canonical: `${DOMAIN}/blog/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          'name': 'Zipytiny Learning Hub & Study Engineering Blog',
          'description': 'Deep dives, evidence-based study workflows, and technical breakthroughs in AI-accelerated learning, active recall, and knowledge synthesis.',
          'url': `${DOMAIN}/blog/`,
          'publisher': orgSchema
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Blog', 'item': `${DOMAIN}/blog/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/features') {
    return {
      title: 'AI Video Summarization & Study Features | Zipytiny',
      description: 'Explore all 9 Zipytiny features: Interactive Mind Maps, Active Recall Flashcards, Structured Study Notes, Practice Quizzes, Podcast Briefings, Meeting Notes, Action Items, Presentation Export, and Executive Summaries.',
      keywords: 'ai video summarizer features, flashcards from video, mind map generator, meeting notes ai, pptx export, action items generator, video to notes features',
      canonical: `${DOMAIN}/features/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'AI Video Summarization & Study Features | Zipytiny',
          'description': 'Explore all 9 Zipytiny features: Interactive Mind Maps, Active Recall Flashcards, Structured Study Notes, Practice Quizzes, Podcast Briefings, Meeting Notes, Action Items, Presentation Export, and Executive Summaries.',
          'url': `${DOMAIN}/features/`,
          'isPartOf': {
            '@type': 'WebSite',
            'name': 'Zipytiny',
            'url': DOMAIN
          }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Features', 'item': `${DOMAIN}/features/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/features/mind-maps') {
    return {
      title: 'Interactive Mind Maps from Any Video or Lecture | Zipytiny',
      description: 'Convert unstructured video lectures and webinars into interactive, hierarchical concept mind maps. Navigate semantic trees and download high-resolution PNGs.',
      keywords: 'video mind map generator, ai concept map, visual study aid, hierarchical knowledge graph, lecture visualizer',
      canonical: `${DOMAIN}/features/mind-maps/`,
      ogType: 'website',
      ogImage: `${DOMAIN}/assets/images/mind_map_feature_highlight_1784543103131.jpg`,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Interactive Mind Maps from Any Video or Lecture | Zipytiny',
          'description': 'Convert unstructured video lectures and webinars into interactive, hierarchical concept mind maps.',
          'url': `${DOMAIN}/features/mind-maps/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny Interactive Mind Map Generator',
          'applicationCategory': 'EducationalApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Features', 'item': `${DOMAIN}/features/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'Interactive Mind Maps', 'item': `${DOMAIN}/features/mind-maps/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/features/flashcards') {
    return {
      title: 'AI Flashcard Generator — Make Flashcards from Videos & PDFs | Zipytiny',
      description: 'Automatically generate flashcards from YouTube videos, lecture recordings, and PDF documents with AI. Practice with spaced repetition and active recall — free, no manual deck-building.',
      keywords: 'ai flashcard generator, make flashcards from video, video to flashcards, flashcards from pdf, active recall flashcards, spaced repetition generator, zipytiny',
      canonical: `${DOMAIN}/features/flashcards/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'AI Flashcard Generator: Turn Videos and PDFs into Flashcards',
          'headline': 'AI Flashcard Generator: Turn Videos and PDFs into Flashcards',
          'description': 'Automatically generate flashcards from YouTube videos, lecture recordings, and PDF documents with AI. Practice with spaced repetition and active recall — free, no manual deck-building.',
          'url': `${DOMAIN}/features/flashcards/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'AI Flashcard Generator: Turn Videos and PDFs into Flashcards',
          'headline': 'AI Flashcard Generator: Turn Videos and PDFs into Flashcards',
          'applicationCategory': 'EducationalApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Features', 'item': `${DOMAIN}/features/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'Flashcards', 'item': `${DOMAIN}/features/flashcards/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/features/study-notes') {
    return {
      title: 'AI Video to Notes Converter — Turn Lectures into Study Notes | Zipytiny',
      description: 'Zipytiny is an AI video note taker that converts lecture videos, recordings, and PDFs into structured study notes in seconds — with timestamps, exports, and zero manual typing.',
      keywords: 'ai video to notes converter, convert video lectures to notes, video note taker, ai lecture notes, structured study notes, zipytiny',
      canonical: `${DOMAIN}/features/study-notes/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Convert Video Lectures to Notes with AI',
          'headline': 'Convert Video Lectures to Notes with AI',
          'description': 'Zipytiny is an AI video note taker that converts lecture videos, recordings, and PDFs into structured study notes in seconds — with timestamps, exports, and zero manual typing.',
          'url': `${DOMAIN}/features/study-notes/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Convert Video Lectures to Notes with AI',
          'headline': 'Convert Video Lectures to Notes with AI',
          'applicationCategory': 'EducationalApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Features', 'item': `${DOMAIN}/features/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'Convert Video Lectures to Notes with AI', 'item': `${DOMAIN}/features/study-notes/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/features/quiz-gen') {
    return {
      title: 'AI Quiz Generator — Create Quizzes from Videos & Text | Zipytiny',
      description: 'Turn YouTube lectures, training videos, and PDF documents into interactive multiple-choice practice quizzes with AI. Instant answers, explanations, and score tracking.',
      keywords: 'ai quiz generator, generate quiz from video, video to quiz, quiz from pdf, interactive knowledge check, practice quiz generator, zipytiny',
      canonical: `${DOMAIN}/features/quiz-gen/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Generate Practice Quizzes from Any Video or Document with AI',
          'headline': 'Generate Practice Quizzes from Any Video or Document with AI',
          'description': 'Turn YouTube lectures, training videos, and PDF documents into interactive multiple-choice practice quizzes with AI. Instant answers, explanations, and score tracking.',
          'url': `${DOMAIN}/features/quiz-gen/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Generate Practice Quizzes from Any Video or Document with AI',
          'headline': 'Generate Practice Quizzes from Any Video or Document with AI',
          'applicationCategory': 'EducationalApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Features', 'item': `${DOMAIN}/features/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'Generate Practice Quizzes from Any Video or Document with AI', 'item': `${DOMAIN}/features/quiz-gen/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/features/podcast-gen') {
    return {
      title: 'Studio-Grade Audio Narrations & Recap Podcasts | Zipytiny',
      description: 'Listen on the go. Transform written video summaries and lecture notes into dynamic, studio-grade audio recaps with natural voiceovers.',
      keywords: 'audio study recap, text to audio podcast, video summary voiceover, on the go audio notes',
      canonical: `${DOMAIN}/features/podcast-gen/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Studio-Grade Audio Narrations & Recap Podcasts | Zipytiny',
          'description': 'Transform written video summaries and lecture notes into dynamic audio recaps with natural voiceovers.',
          'url': `${DOMAIN}/features/podcast-gen/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny Audio Podcast Briefing Generator',
          'applicationCategory': 'EducationalApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Features', 'item': `${DOMAIN}/features/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'Podcast Briefings', 'item': `${DOMAIN}/features/podcast-gen/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/features/meeting-notes') {
    return {
      title: 'AI Meeting Notes & Key Takeaways Generator | Zipytiny',
      description: 'Convert hours of meeting recordings, Zoom calls, and client webinars into executive briefs and action items in seconds.',
      keywords: 'ai meeting notes, zoom meeting summarizer, client webinar notes, meeting takeaways ai',
      canonical: `${DOMAIN}/features/meeting-notes/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'AI Meeting Notes & Key Takeaways Generator | Zipytiny',
          'description': 'Convert meeting recordings, Zoom calls, and client webinars into executive briefs and action items.',
          'url': `${DOMAIN}/features/meeting-notes/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny AI Meeting Notes Generator',
          'applicationCategory': 'BusinessApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Features', 'item': `${DOMAIN}/features/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'Meeting Notes', 'item': `${DOMAIN}/features/meeting-notes/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/features/action-items') {
    return {
      title: 'Automated Action Item & Task Extractor | Zipytiny',
      description: 'Never drop a deliverable. Automatically identify, categorize, and assign action items, next steps, and project milestones from recorded meetings.',
      keywords: 'meeting action items, task extractor ai, next steps from recording, meeting deliverables tracker',
      canonical: `${DOMAIN}/features/action-items/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Automated Action Item & Task Extractor | Zipytiny',
          'description': 'Automatically identify, categorize, and assign action items and next steps from recorded meetings.',
          'url': `${DOMAIN}/features/action-items/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny Action Item Extractor',
          'applicationCategory': 'BusinessApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Features', 'item': `${DOMAIN}/features/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'Action Items', 'item': `${DOMAIN}/features/action-items/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/features/presentation-export') {
    return {
      title: 'AI Presentation & PowerPoint PPTX Slide Generator | Zipytiny',
      description: 'Export beautifully formatted PowerPoint slide decks (.pptx) and executive briefs directly from summarized video lectures and documents.',
      keywords: 'video to powerpoint, ai slide generator, pptx presentation export, lecture to slides',
      canonical: `${DOMAIN}/features/presentation-export/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'AI Presentation & PowerPoint PPTX Slide Generator | Zipytiny',
          'description': 'Export beautifully formatted PowerPoint slide decks (.pptx) directly from summarized video lectures and documents.',
          'url': `${DOMAIN}/features/presentation-export/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny Presentation & Slide Generator',
          'applicationCategory': 'BusinessApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Features', 'item': `${DOMAIN}/features/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'Presentation Export', 'item': `${DOMAIN}/features/presentation-export/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/features/executive-summaries') {
    return {
      title: 'Executive Summaries & High-Level Briefs | Zipytiny',
      description: 'Get the bottom line without the fluff. Distill multi-hour presentations, financial reports, and keynote addresses into 1-page executive briefs.',
      keywords: 'executive summary ai, 1 page brief generator, keynote address summary, video executive brief',
      canonical: `${DOMAIN}/features/executive-summaries/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Executive Summaries & High-Level Briefs | Zipytiny',
          'description': 'Distill multi-hour presentations, financial reports, and keynote addresses into 1-page executive briefs.',
          'url': `${DOMAIN}/features/executive-summaries/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny Executive Summary Generator',
          'applicationCategory': 'BusinessApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Features', 'item': `${DOMAIN}/features/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'Executive Summaries', 'item': `${DOMAIN}/features/executive-summaries/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/use-cases/sales-calls') {
    return {
      title: 'Turn Sales Calls Into Follow-Up Emails in Seconds | Zipytiny',
      description: 'Upload any sales call recording and get a ready-to-send follow-up email, CRM-ready notes, and action items — no manual typing, no missed details.',
      keywords: 'turn sales calls into follow-up emails, sales call follow-up generator, call recording to email, crm sales notes ai, sales call action items, meeting follow-up email template, ai sales email writer',
      canonical: `${DOMAIN}/use-cases/sales-calls/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Turn Sales Calls Into Follow-Up Emails in Seconds | Zipytiny',
          'description': 'Upload any sales call recording and get a ready-to-send follow-up email, CRM-ready notes, and action items — no manual typing, no missed details.',
          'url': `${DOMAIN}/use-cases/sales-calls/`,
          'isPartOf': {
            '@type': 'WebSite',
            'name': 'Zipytiny',
            'url': DOMAIN
          }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny Sales Call Follow-Up Generator',
          'applicationCategory': 'BusinessApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'USD'
          }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            {
              '@type': 'ListItem',
              'position': 1,
              'name': 'Home',
              'item': `${DOMAIN}/`
            },
            {
              '@type': 'ListItem',
              'position': 2,
              'name': 'Use Cases',
              'item': `${DOMAIN}/use-cases/sales-calls/`
            },
            {
              '@type': 'ListItem',
              'position': 3,
              'name': 'Sales Calls to Follow-Up Email',
              'item': `${DOMAIN}/use-cases/sales-calls/`
            }
          ]
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': [
            {
              '@type': 'Question',
              'name': 'How does Zipytiny turn sales call recordings into ready-to-send follow-up emails?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Zipytiny processes your uploaded sales call recording (or pasted transcript) using advanced semantic models. It automatically identifies the key participants, extracts pain points, captures agreed commitments, and drafts a structured, polite follow-up email with explicit action items and deadlines in seconds.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Which video and audio call recording formats are supported?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Zipytiny supports all major recording file formats including MP4, MOV, WEBM, MP3, WAV, M4A, and AAC up to 30 MB. You can upload recordings directly from Zoom, Microsoft Teams, Google Meet, Gong, Chorus, Loom, or paste raw text transcripts directly.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Can I copy the extracted notes into my CRM?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Yes. Zipytiny generates clean, structured summary notes including key pain points, decision criteria, and next milestones. You can copy them in one click and paste them directly into any CRM notes or activity field.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Are client sales conversations and confidential audio recordings kept private?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Yes. Zipytiny enforces strict privacy standards. Uploaded audio and transcripts are processed in memory with strict zero-retention and are never used to train public foundation models.'
              }
            }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/use-cases/client-meeting-recap') {
    return {
      title: 'Turn Client Calls Into Polished Recaps Clients Actually Read | Zipytiny',
      description: 'Upload your client meeting recording and get a clean, professional recap ready to send — no more scrambling to write it up before you forget the details.',
      keywords: 'client meeting recap, client call summary, client follow-up email, meeting recap generator, client meeting action items, project recap ai',
      canonical: `${DOMAIN}/use-cases/client-meeting-recap/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Turn Client Calls Into Polished Recaps Clients Actually Read | Zipytiny',
          'description': 'Upload your client meeting recording and get a clean, professional recap ready to send — no more scrambling to write it up before you forget the details.',
          'url': `${DOMAIN}/use-cases/client-meeting-recap/`,
          'isPartOf': {
            '@type': 'WebSite',
            'name': 'Zipytiny',
            'url': DOMAIN
          }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny Client Meeting Recap Generator',
          'applicationCategory': 'BusinessApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'USD'
          }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            {
              '@type': 'ListItem',
              'position': 1,
              'name': 'Home',
              'item': `${DOMAIN}/`
            },
            {
              '@type': 'ListItem',
              'position': 2,
              'name': 'Use Cases',
              'item': `${DOMAIN}/use-cases/client-meeting-recap/`
            },
            {
              '@type': 'ListItem',
              'position': 3,
              'name': 'Client Meeting Recaps',
              'item': `${DOMAIN}/use-cases/client-meeting-recap/`
            }
          ]
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': [
            {
              '@type': 'Question',
              'name': 'Does it work if multiple people spoke on the call?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Yes. You can upload multi-person call recordings from Zoom, Microsoft Teams, Google Meet, or Webex. Zipytiny processes the entire conversation into a cohesive, structured recap with decisions, open questions, and action items, though it produces a unified summary rather than labeling individual voice tracks per speaker.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Can I customize the tone of the recap?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Recaps are generated in a clean, objective professional format by default. You can easily edit or tweak the copy in one click before pasting it into your email client or document.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Can I add my own branding to the export?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Exports are generated in clean, standard formatted PDF and Microsoft Word documents. You can also copy the formatted markdown or plain text directly into your agency or company\'s branded client templates or client portal.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Is my client\'s call data private?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Yes. Zipytiny enforces strict privacy standards. Uploaded audio, video files, and transcripts are processed in memory with strict zero-retention and are never used to train public foundation models.'
              }
            }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/use-cases/recording-to-powerpoint') {
    return {
      title: 'Turn Any Zoom Recording Into a PowerPoint Deck | Zipytiny',
      description: 'Upload a Zoom, Teams, or Meet recording and get a formatted PowerPoint deck with key points, decisions, and next steps — ready to share or present.',
      keywords: 'turn zoom recording into powerpoint, meeting to slides ai, convert call recording to presentation, zoom to powerpoint generator, ai pptx deck generator',
      canonical: `${DOMAIN}/use-cases/recording-to-powerpoint/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Turn Any Zoom Recording Into a PowerPoint Deck | Zipytiny',
          'url': `${DOMAIN}/use-cases/recording-to-powerpoint/`,
          'description': 'Upload a Zoom, Teams, or Meet recording and get a formatted PowerPoint deck with key points, decisions, and next steps — ready to share or present.',
          'isPartOf': {
            '@type': 'WebSite',
            'name': 'Zipytiny',
            'url': DOMAIN
          }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny Recording to PowerPoint Deck Generator',
          'applicationCategory': 'BusinessApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'USD'
          }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            {
              '@type': 'ListItem',
              'position': 1,
              'name': 'Home',
              'item': `${DOMAIN}/`
            },
            {
              '@type': 'ListItem',
              'position': 2,
              'name': 'Use Cases',
              'item': `${DOMAIN}/use-cases/recording-to-powerpoint/`
            },
            {
              '@type': 'ListItem',
              'position': 3,
              'name': 'Recording to PowerPoint',
              'item': `${DOMAIN}/use-cases/recording-to-powerpoint/`
            }
          ]
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': [
            {
              '@type': 'Question',
              'name': 'Does the deck look like a generic AI template, or can I customize it?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'You have full control over the visual presentation. Zipytiny provides multiple presentation themes (including Corporate Blue, Minimalist, Apple Keynote, and Modern Gradient) and customizable slide layouts. You can edit slide headlines, bullet points, and speaker notes directly in your browser before exporting, or use AI prompts to regenerate specific sections.'
              }
            },
            {
              '@type': 'Question',
              'name': 'What file size and recording length are supported?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'There is no separate duration limit — any audio or video recording under 30 MB works, regardless of length (supported formats include MP4, WEBM, MOV, MP3, WAV, M4A, and AAC). For longer recordings that exceed 30 MB, you can paste the text transcript directly or import a YouTube video link of any length.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Can I edit the slides after they\'re generated?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Yes. Zipytiny generates native, fully editable Microsoft PowerPoint (.pptx) files. When you open the exported deck in PowerPoint, Google Slides, or Apple Keynote, every text box, bullet point, theme color, and speaker note is completely editable.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Is my recording data private?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Yes. Zipytiny enforces strict privacy standards. Uploaded audio, video files, and transcripts are processed in memory with strict zero-retention and are never used to train public foundation models.'
              }
            }
          ]
        }
      ]
    };
  }

  // 11d. COMPARISON: /vs/otter-ai
  if (cleanPath === '/vs/otter-ai') {
    return {
      title: 'Zipytiny vs Otter.ai: Which One Actually Fits Your Workflow? | Zipytiny',
      description: 'Otter records live meetings with a bot. Zipytiny turns any recording, PDF, or document into a finished output. Here\'s an honest breakdown of the difference.',
      keywords: 'zipytiny vs otter ai, otter ai alternative, meeting transcription vs summary, meeting recording to powerpoint, post meeting summarizer, otter ai comparison',
      canonical: `${DOMAIN}/vs/otter-ai/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Zipytiny vs Otter.ai: Which One Actually Fits Your Workflow? | Zipytiny',
          'url': `${DOMAIN}/vs/otter-ai/`,
          'description': 'Otter records live meetings with a bot. Zipytiny turns any recording, PDF, or document into a finished output. Here\'s an honest breakdown of the difference.',
          'isPartOf': {
            '@type': 'WebSite',
            'name': 'Zipytiny',
            'url': DOMAIN
          }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny Knowledge Workspace',
          'applicationCategory': 'BusinessApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'USD'
          }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            {
              '@type': 'ListItem',
              'position': 1,
              'name': 'Home',
              'item': `${DOMAIN}/`
            },
            {
              '@type': 'ListItem',
              'position': 2,
              'name': 'Comparisons',
              'item': `${DOMAIN}/vs/otter-ai/`
            },
            {
              '@type': 'ListItem',
              'position': 3,
              'name': 'Zipytiny vs Otter.ai',
              'item': `${DOMAIN}/vs/otter-ai/`
            }
          ]
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': [
            {
              '@type': 'Question',
              'name': 'Does Zipytiny do everything Otter does?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'No — Zipytiny doesn\'t join live meetings or provide real-time transcription; it processes recordings and documents after the fact.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Can I use Zipytiny with recordings I already made in Otter?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Yes — Zipytiny accepts pasted text transcripts as input, so an Otter transcript export can be pasted directly in to generate a follow-up email, summary, or action items.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Is Zipytiny cheaper than Otter?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Zipytiny\'s entry paid plan is $9.99/month; Otter\'s Pro plan is $16.99/month ($8.33/month billed annually). Exact value depends on which features you need.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Why doesn\'t Zipytiny have speaker tagging?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Zipytiny currently focuses on structured outputs rather than per-speaker transcription.'
              }
            }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/chrome-extension') {
    return {
      title: 'Zipytiny Chrome Extension — Summarize YouTube Videos in 1 Click | Zipytiny',
      description: 'Install the Zipytiny Chrome Extension to summarize YouTube lectures, videos, and webinars directly inside your browser. Generate notes, flashcards, and quizzes without leaving YouTube.',
      keywords: 'zipytiny chrome extension, youtube summarizer extension, video notes chrome extension, lecture summarizer plugin, chrome ai summarizer',
      canonical: `${DOMAIN}/chrome-extension/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Zipytiny Chrome Extension — Summarize YouTube Videos in 1 Click | Zipytiny',
          'description': 'Install the Zipytiny Chrome Extension to summarize YouTube lectures and webinars directly inside your browser.',
          'url': `${DOMAIN}/chrome-extension/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny Chrome Extension',
          'applicationCategory': 'BrowserApplication, EducationalApplication',
          'operatingSystem': 'Google Chrome, Brave, Chromium',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Chrome Extension', 'item': `${DOMAIN}/chrome-extension/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/tools') {
    return {
      title: 'Free AI Learning Tools & Lecture Summarizer Sandbox | Zipytiny',
      description: 'Test Zipytiny\'s interactive AI study tools: YouTube lecture summarizer, PDF study guide generator, and Feynman AI tutor with instant flashcards and quizzes.',
      keywords: 'ai study tools, youtube lecture summarizer, pdf study guide generator, feynman ai tutor, free video notes, zipytiny tools',
      canonical: `${DOMAIN}/tools/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          'name': 'Free AI Learning Tools & Sandbox | Zipytiny',
          'description': 'Test Zipytiny interactive AI study tools: YouTube lecture summarizer, PDF study guide generator, and Feynman AI tutor.',
          'url': `${DOMAIN}/tools/`,
          'publisher': orgSchema
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Tools', 'item': `${DOMAIN}/tools/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/tools/youtube-lecture-summarizer') {
    return {
      title: 'AI YouTube Lecture Summarizer & Study Workspace | Zipytiny',
      description: 'Turn long lecture videos and tutorials into structured, interactive study materials. Instantly generate chronological outlines, spaced repetition flashcards, and quizzes.',
      keywords: 'youtube lecture summarizer, video to study notes, youtube video summary ai, lecture notes generator, active recall flashcards',
      canonical: `${DOMAIN}/tools/youtube-lecture-summarizer/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'AI YouTube Lecture Summarizer & Study Workspace | Zipytiny',
          'description': 'Turn long lecture videos and tutorials into structured study materials with chronological outlines, flashcards, and quizzes.',
          'url': `${DOMAIN}/tools/youtube-lecture-summarizer/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny YouTube Lecture Summarizer',
          'applicationCategory': 'EducationalApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Tools', 'item': `${DOMAIN}/tools/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'YouTube Summarizer', 'item': `${DOMAIN}/tools/youtube-lecture-summarizer/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/tools/pdf-study-guide-generator') {
    return {
      title: 'AI PDF Study Guide Generator & Workspace | Zipytiny',
      description: 'Automatically parse lecture slides, research papers, and textbook chapters. Convert dense handouts into readable syllabus outlines, simplified concepts, and test sheets.',
      keywords: 'pdf study guide generator, pdf to notes ai, lecture slide summarizer, textbook chapter notes, ai study syllabus',
      canonical: `${DOMAIN}/tools/pdf-study-guide-generator/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'AI PDF Study Guide Generator & Workspace | Zipytiny',
          'description': 'Automatically parse lecture slides, research papers, and textbook chapters into syllabus outlines and testing sheets.',
          'url': `${DOMAIN}/tools/pdf-study-guide-generator/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny PDF Study Guide Generator',
          'applicationCategory': 'EducationalApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Tools', 'item': `${DOMAIN}/tools/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'PDF Study Guide Generator', 'item': `${DOMAIN}/tools/pdf-study-guide-generator/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/tools/interactive-ai-tutor') {
    return {
      title: 'Interactive AI Tutor & Feynman Assistant | Zipytiny',
      description: 'Master complex topics through active Socratic dialogue. Ask the Feynman AI Tutor anything, explore intuitive analogies, and target mental gaps using cognitive sciences.',
      keywords: 'feynman ai tutor, socratic ai tutor, interactive study assistant, concept explanation ai, active learning tutor',
      canonical: `${DOMAIN}/tools/interactive-ai-tutor/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Interactive AI Tutor & Feynman Assistant | Zipytiny',
          'description': 'Master complex topics through active Socratic dialogue with the Feynman AI Tutor.',
          'url': `${DOMAIN}/tools/interactive-ai-tutor/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny Feynman AI Tutor',
          'applicationCategory': 'EducationalApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'USD' }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Tools', 'item': `${DOMAIN}/tools/` },
            { '@type': 'ListItem', 'position': 3, 'name': 'Feynman AI Tutor', 'item': `${DOMAIN}/tools/interactive-ai-tutor/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/pricing') {
    return {
      title: 'Simple, Transparent Pricing — Free & Pro Study Workspace Plans | Zipytiny',
      description: `Try Zipytiny completely free. Upgrade to Pro for $${PRO_PLAN_MONTHLY_PRICE}/month for unlimited summaries, interactive mind maps, PPTX slide exports, and audio narrations.`,
      keywords: 'zipytiny pricing, free ai video summarizer, pro study tools subscription, affordable ai study guide',
      canonical: `${DOMAIN}/pricing/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Simple, Transparent Pricing — Free & Pro Plans | Zipytiny',
          'description': `Try Zipytiny completely free. Upgrade to Pro for $${PRO_PLAN_MONTHLY_PRICE}/month for unlimited summaries.`,
          'url': `${DOMAIN}/pricing/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          'name': 'Zipytiny Knowledge Workspace',
          'applicationCategory': 'EducationalApplication, ProductivityApplication',
          'operatingSystem': 'Web, Chrome Extension',
          'offers': [
            {
              '@type': 'Offer',
              'name': 'Free Plan',
              'price': `${FREE_PLAN_PRICE}`,
              'priceCurrency': 'USD'
            },
            {
              '@type': 'Offer',
              'name': 'Pro Monthly Plan',
              'price': `${PRO_PLAN_MONTHLY_PRICE}`,
              'priceCurrency': 'USD',
              'priceSpecification': {
                '@type': 'UnitPriceSpecification',
                'price': `${PRO_PLAN_MONTHLY_PRICE}`,
                'priceCurrency': 'USD',
                'unitText': 'MONTH'
              }
            }
          ]
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'Pricing', 'item': `${DOMAIN}/pricing/` }
          ]
        }
      ]
    };
  }

  if (cleanPath === '/faq') {
    return {
      title: 'Frequently Asked Questions & Help Center | Zipytiny',
      description: 'Find answers to common questions about Zipytiny: supported file types, video length limits, privacy and data security, exports, and billing.',
      keywords: 'zipytiny faq, ai summarizer help, supported video formats, zipytiny questions, privacy policy',
      canonical: `${DOMAIN}/faq/`,
      ogType: 'website',
      ogImage: DEFAULT_OG_IMAGE,
      jsonLd: [
        orgSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          'name': 'Frequently Asked Questions & Help Center | Zipytiny',
          'description': 'Find answers to common questions about Zipytiny: supported file types, video length limits, privacy and data security, exports, and billing.',
          'url': `${DOMAIN}/faq/`,
          'isPartOf': { '@type': 'WebSite', 'name': 'Zipytiny', 'url': DOMAIN }
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${DOMAIN}/` },
            { '@type': 'ListItem', 'position': 2, 'name': 'FAQ', 'item': `${DOMAIN}/faq/` }
          ]
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': [
            {
              '@type': 'Question',
              'name': 'What file formats and links can Zipytiny summarize?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Zipytiny supports YouTube video links, Vimeo URLs, PDF documents, PowerPoint slide decks (.pptx), audio recordings (.mp3, .wav, .m4a), and recorded meetings from Zoom, Teams, and Google Meet.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Can I export study materials to Anki or Notion?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Yes! Flashcards can be exported directly as Anki-compatible decks or copied as formatted Markdown for seamless import into Notion, Obsidian, or Word.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Is my uploaded content and data kept private?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Yes. Zipytiny processes uploaded files in memory with strict data privacy. Uploaded files and transcripts are never used to train public foundation models.'
              }
            }
          ]
        }
      ]
    };
  }

  // Default Homepage: /
  return {
    title: 'Zipytiny — Stop rewatching. Start remembering.',
    description: 'Zipytiny turns any video, PDF, slide deck, or meeting recording into notes, flashcards, and mind maps — so you spend less time reviewing and more time actually knowing it.',
    keywords: 'ai study notes, youtube video summarizer, pdf to flashcards, lecture to mind map, spaced repetition, instant study guide, zoom recording summarizer',
    canonical: `${DOMAIN}/`,
    ogType: 'website',
    ogImage: DEFAULT_OG_IMAGE,
    jsonLd: [
      orgSchema,
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'Zipytiny',
        'url': DOMAIN,
        'potentialAction': {
          '@type': 'SearchAction',
          'target': `${DOMAIN}/?url={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        'name': 'Zipytiny Knowledge Workspace',
        'applicationCategory': 'EducationalApplication, ProductivityApplication',
        'operatingSystem': 'Web, Chrome Extension',
        'offers': {
          '@type': 'Offer',
          'price': '0',
          'priceCurrency': 'USD'
        }
      }
    ]
  };
}

export function getRouteSeoMeta(urlPath: string): RouteSeoMeta {
  const meta = resolveRouteSeoMeta(urlPath);
  return {
    ...meta,
    canonical: normalizeApexCanonical(meta.canonical)
  };
}
